/**
 * プリセット一覧テーブル（Presentation）
 */

import { Button } from "components/ui/Button";
import { Link as UiLink } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import type { Preset } from "hooks/use-preset-api";
import type { FC } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";

type PresetsTableProps = {
  presets: Preset[];
  onToggleActive: (presetId: string) => Promise<void>;
  onDelete: (presetId: string) => Promise<void>;
  isToggling: boolean;
  isDeleting: boolean;
};

const PRESET_TYPE_LABELS: Record<string, string> = {
  hr: "人材",
  matter: "案件",
};

/**
 * プリセット一覧テーブル
 * @param {PresetsTableProps} props コンポーネントProps
 * @return {JSX.Element} テーブル要素
 */
export const PresetsTable: FC<PresetsTableProps> = ({
  presets,
  onToggleActive,
  onDelete,
  isToggling,
  isDeleting,
}) => {
  const [actionId, setActionId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleToggleActive = async (presetId: string) => {
    setActionId(presetId);
    try {
      await onToggleActive(presetId);
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (presetId: string) => {
    setActionId(presetId);
    try {
      await onDelete(presetId);
    } finally {
      setActionId(null);
      setDeleteConfirm(null);
    }
  };

  if (presets.length === 0) {
    return (
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <p className="text-base-content/60">
            プリセットがまだありません。「+ 新規作成」から追加してください。
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>名前</Table.HeaderCell>
              <Table.HeaderCell>種別</Table.HeaderCell>
              <Table.HeaderCell>スキル</Table.HeaderCell>
              <Table.HeaderCell>ステータス</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {presets.map(preset => (
              <Table.Row key={preset.id}>
                <Table.Cell>
                  <UiLink to={`/settings/presets/${preset.id}`}>
                    {preset.name}
                  </UiLink>
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge tone="tag-neutral">
                    {PRESET_TYPE_LABELS[preset.presetType] || preset.presetType}
                  </StatusBadge>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex flex-wrap gap-1 max-w-xs">
                    {preset.skills?.slice(0, 3).map(skill => (
                      <span
                        key={skill}
                        className="badge badge-outline badge-xs"
                      >
                        {skill}
                      </span>
                    ))}
                    {preset.skills && preset.skills.length > 3 && (
                      <StatusBadge tone="soft-neutral">
                        +{preset.skills.length - 3}
                      </StatusBadge>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  {preset.isActive ? (
                    <StatusBadge tone="soft-success">有効</StatusBadge>
                  ) : (
                    <StatusBadge tone="soft-neutral">無効</StatusBadge>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      onClick={() => handleToggleActive(preset.id)}
                      disabled={isToggling && actionId === preset.id}
                    >
                      {isToggling && actionId === preset.id ? (
                        <Loading size="xs" />
                      ) : preset.isActive ? (
                        "無効化"
                      ) : (
                        "有効化"
                      )}
                    </Button>
                    <Button asChild variant="outline" size="xs">
                      <Link to={`/settings/presets/${preset.id}/edit`}>
                        編集
                      </Link>
                    </Button>
                    {!preset.isActive && (
                      <Button
                        type="button"
                        variant="destructive"
                        isOutline
                        size="xs"
                        onClick={() => setDeleteConfirm(preset.id)}
                        disabled={isDeleting && actionId === preset.id}
                      >
                        削除
                      </Button>
                    )}
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </div>

      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">プリセットを削除しますか？</h3>
            <p className="py-4">この操作は取り消せません。</p>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isDeleting}
              >
                {isDeleting && actionId === deleteConfirm ? (
                  <Loading size="sm" />
                ) : (
                  "削除"
                )}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isDeleting && setDeleteConfirm(null)}
          />
        </div>
      )}
    </>
  );
};
